import React, { useMemo, useState, useCallback, useRef, useEffect } from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useDraggable, useDroppable } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { useDeckViewState } from '../hooks/useDeckViewState.js';
import { useDeckDragHandlers } from '../hooks/useDeckDragHandlers.js';
import { useDelayedHover } from '../hooks/useDelayedHover.js';
import { useShouldBeTransparent } from '../hooks/useShouldBeTransparent.js';
import { useDeckCardActions } from '../hooks/useDeckCardActions';

import CategoryHeader from '../../../ui/CategoryHeader';
import ConfirmDialog from '../../../ui/ConfirmDialog';
import { formatPrice } from '../utils/formatPrice';
import CardPreview from '../components/CardPreview.js';
import CardContextMenu from '../components/CardContextMenu.js';
import ManaSymbols from '../../../ui/ManaSymbols';
import { useAuth } from '../../../context/AuthContext';

const HOVER_DELAY_MS = 200;

// --- CompactCard: Einzelne Kartenzeile ---

function CompactCard({
  card,
  isOwnProfile,
  canEditQuantity,
  onQuantityDelta,
  onSetAsCommander,
  onShowDetails,
  onSwitchPrint,
  onPreviewStart,
  onPreviewEnd,
  activePreviewCardId,
  highlightedNewCardId,
  deckLabels,
  cardLabels,
  onAssignLabel,
  onRemoveLabel,
  onNewLabelRequest,
}) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: `card-${card.id}`,
    data: { type: 'card', card },
  });

  const cardId = card.scryfallId || card.id;
  const posRef = useRef(null);
  const [menuOpen, setMenuOpen] = useState(false);

  // Basic Lands (z.B. Wald, Insel) bekommen die Menge inline mit +/- (wie in der
  // Tabelle) — das sind die Karten, deren Anzahl man beim Deckbau am häufigsten
  // feinjustiert. Erkennung wie im Export-Modal: Supertyp "Basic" + Typ "Land".
  const isBasicLand =
    (card.superTypes || []).some((st) => String(st).toLowerCase() === 'basic') &&
    (card.types || []).some((t) => String(t).toLowerCase() === 'land');
  const showInlineQty = isBasicLand && canEditQuantity;

  const extraMenuItems = canEditQuantity
    ? [
        // Bei Basic Lands liegen +1/-1 bereits inline in der Zeile — im Menü nur "Entfernen".
        ...(showInlineQty ? [] : [
          { label: '+1', onClick: () => onQuantityDelta(cardId, 1) },
          { label: '-1', onClick: () => onQuantityDelta(cardId, -1) },
        ]),
        { label: 'Entfernen', onClick: () => onQuantityDelta(cardId, -card.quantity) },
      ]
    : [];

  const handleSetCommander = useCallback(() => {
    onSetAsCommander(card.id, true);
  }, [card.id, onSetAsCommander]);

  const {
    isHovered: isPreviewHovered,
    onMouseEnter: onHoverEnter,
    onMouseLeave: onHoverLeave,
  } = useDelayedHover({ enterDelay: HOVER_DELAY_MS, leaveDelay: HOVER_DELAY_MS });

  // Preview nur anzeigen wenn Menü geschlossen ist
  useEffect(() => {
    if (isPreviewHovered && posRef.current && !menuOpen) {
      onPreviewStart(card, posRef.current);
    } else if ((!isPreviewHovered || menuOpen) && activePreviewCardId === card.id) {
      onPreviewEnd();
    }
  }, [isPreviewHovered, menuOpen, card, onPreviewStart, onPreviewEnd, activePreviewCardId]);

  const handleMenuToggle = useCallback((isOpen) => {
    setMenuOpen(isOpen);
  }, []);

  const handleMouseEnter = useCallback((e) => {
    posRef.current = { x: e.clientX, y: e.clientY };
    if (!menuOpen) onHoverEnter();
  }, [onHoverEnter, menuOpen]);

  const handleMouseMove = useCallback((e) => {
    posRef.current = { x: e.clientX, y: e.clientY };
    if (isPreviewHovered && !menuOpen) {
      onPreviewStart(card, { x: e.clientX, y: e.clientY });
    }
  }, [isPreviewHovered, menuOpen, card, onPreviewStart]);

  return (
    <div
      ref={setNodeRef}
      {...attributes}
      {...listeners}
      data-card-id={card.id || card.scryfallId}
      className={`compact-card-row${isDragging ? ' compact-card-row--dragging' : ''}${(card.id || card.scryfallId) === highlightedNewCardId ? ' card-just-added' : ''}`}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={onHoverLeave}
      onMouseMove={handleMouseMove}
    >
      <span className="compact-card-name" title={card.name}>{card.name}</span>
      <span className="compact-card-mana">
        <ManaSymbols manaCost={card.manaCost} size={14} />
      </span>
      {/* Menge rechts, direkt vor dem ⋮-Menü. Basic Lands zeigen die
          Mengensteuerung immer (wie die Tabelle) — Kern-Editieraktion für Länder;
          die +/-Buttons tragen denselben rahmenlosen Stil wie der ⋮-Trigger. Alle
          anderen Karten zeigen die reine Anzahl, sobald sie größer als 1 ist. */}
      {showInlineQty ? (
        <span className="compact-card-qty-controls">
          <button
            type="button"
            className="compact-qty-btn"
            onClick={(e) => { e.stopPropagation(); onQuantityDelta(cardId, -1); }}
            onPointerDown={(e) => e.stopPropagation()}
            aria-label="Eine Kopie entfernen"
          >
            −
          </button>
          <span className="compact-card-qty">{card.quantity}</span>
          <button
            type="button"
            className="compact-qty-btn"
            onClick={(e) => { e.stopPropagation(); onQuantityDelta(cardId, 1); }}
            onPointerDown={(e) => e.stopPropagation()}
            aria-label="Eine Kopie hinzufügen"
          >
            +
          </button>
        </span>
      ) : (
        (card.quantity || 0) > 1 && <span className="compact-card-qty">{card.quantity}</span>
      )}
      {/* Ohne oracleId bliebe im fremden Deck kein Eintrag übrig — der Trigger wäre
          ein leeres Popup. */}
      {(isOwnProfile || (onShowDetails && card.oracleId)) && (
        <CardContextMenu
          card={card}
          onShowDetails={onShowDetails && card.oracleId ? onShowDetails : undefined}
          onSwitchPrint={onSwitchPrint && card.oracleId ? onSwitchPrint : undefined}
          onSetCommander={isOwnProfile ? handleSetCommander : undefined}
          extraItems={extraMenuItems}
          onOpenChange={handleMenuToggle}
          deckLabels={deckLabels}
          cardLabels={cardLabels}
          onAssignLabel={(label) => onAssignLabel(card.deckCardEntryId, label)}
          onRemoveLabel={(labelId) => onRemoveLabel(card.deckCardEntryId, labelId)}
          onNewLabel={isOwnProfile ? () => onNewLabelRequest(card.deckCardEntryId) : undefined}
        />
      )}
    </div>
  );
}

// --- CompactCategory: Ein Kategorie-Block ---

function CompactCategory({
  category,
  categoryId,
  excluded,
  cards,
  isOwnProfile,
  canEditQuantity,
  onQuantityDelta,
  onSetAsCommander,
  onShowDetails,
  onSwitchPrint,
  onPreviewStart,
  onPreviewEnd,
  activePreviewCardId,
  isDraggingCategory,
  isDroppable,
  isDropTarget = false,
  externalDragging = false,
  externalDropProps,
  highlightedNewCardId,
  deckLabels,
  getCardLabels,
  onAssignLabel,
  onRemoveLabel,
  onNewLabelRequest,
  canRemove,
  handleRemoveCategory,
  stickyTop = 0,
  strategy,
  renameCategory,
  onCategoryRenamed,
}) {
  const [isCollapsed, setIsCollapsed] = useState(false);

  const totalCards = useMemo(
    () => cards.reduce((sum, c) => sum + (c.quantity || 1), 0),
    [cards]
  );
  const totalPrice = useMemo(
    () => cards.reduce((sum, c) => sum + ((Number(c.price) || 0) * (Number(c.quantity) || 0)), 0),
    [cards]
  );

  // WICHTIG: Hook-Reihenfolge — useDroppable VOR useSortable. Beide Droppables
  // teilen sich denselben DOM-Knoten via setCombinedRef. Bei pointerWithin
  // liefert dnd-kit Collisions in Registrierungs-Reihenfolge zurück; das zuerst
  // gehookte Droppable gewinnt als `over`. Damit wir lokal `isOver` aus
  // useDroppable lesen können, muss es zuerst kommen — gleiches Muster wie in
  // Stacks/Rows/Table.
  const { setNodeRef: setDropRef, isOver } = useDroppable({
    id: `category-${category}`,
    data: { category, categoryId },
    disabled: !isDroppable,
  });

  const {
    attributes,
    listeners,
    setNodeRef: setSortableRef,
    transform,
    transition,
    isDragging: isSortDragging,
  } = useSortable({
    id: `cat-${category}`,
    data: { type: 'category', category },
  });

  const setCombinedRef = useCallback((el) => {
    setDropRef(el);
    setSortableRef(el);
  }, [setDropRef, setSortableRef]);

  const sortableStyle = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isSortDragging ? 0.5 : 1,
  };

  // Drop-Target-Highlight: bei Karten-Drag (isOver) sowie bei Kategorie-Drag (isDraggingCategory)
  const showAsDropTarget = isOver || isDraggingCategory || isDropTarget;

  // Rename nur für echte, persistierte Kategorien des eigenen Decks
  // (nicht 'other'/Nicht-Kategorie-Gruppierungen, nicht Commander — der hat keinen Header hier).
  const canRenameCategory = Boolean(isOwnProfile && strategy?.persistent && categoryId != null);

  const handleRenameCategory = useCallback(async (nextName) => {
    const result = await renameCategory?.(categoryId, nextName);
    onCategoryRenamed?.(category, result);
  }, [categoryId, category, renameCategory, onCategoryRenamed]);

  return (
    <div
      ref={setCombinedRef}
      {...attributes}
      data-drop-group={category}
      data-external-target={isDropTarget && externalDragging ? 'true' : undefined}
      {...externalDropProps}
      className={`compact-category${showAsDropTarget ? ' compact-category--drop-target' : ''}${externalDragging && !showAsDropTarget ? ' compact-category--external-hint' : ''}`}
      style={sortableStyle}
    >
      <CategoryHeader
        name={category}
        count={totalCards}
        price={formatPrice(totalPrice)}
        excluded={excluded}
        dragHandle={isOwnProfile}
        dragListeners={isOwnProfile ? listeners : undefined}
        isDropTarget={showAsDropTarget}
        stickyTop={stickyTop}
        collapsible
        collapsed={isCollapsed}
        onToggleCollapse={() => setIsCollapsed((v) => !v)}
        canRename={canRenameCategory}
        onRename={(next) => handleRenameCategory(next)}
        canRemove={canRemove && isOwnProfile}
        onRemove={() => handleRemoveCategory?.(category)}
      />

      <div
        className="compact-category__cards"
        style={{
          maxHeight: isCollapsed ? 0 : 'none',
          overflow: isCollapsed ? 'hidden' : 'visible',
          transition: 'max-height 0.2s ease',
        }}
      >
        {cards.map((card) => (
          <CompactCard
            key={card.id}
            card={card}
            isOwnProfile={isOwnProfile}
            canEditQuantity={canEditQuantity}
            onQuantityDelta={onQuantityDelta}
            onSetAsCommander={onSetAsCommander}
            onShowDetails={onShowDetails}
            onSwitchPrint={onSwitchPrint}
            onPreviewStart={onPreviewStart}
            onPreviewEnd={onPreviewEnd}
            activePreviewCardId={activePreviewCardId}
            highlightedNewCardId={highlightedNewCardId}
            deckLabels={deckLabels}
            cardLabels={getCardLabels(card.deckCardEntryId)}
            onAssignLabel={onAssignLabel}
            onRemoveLabel={onRemoveLabel}
            onNewLabelRequest={onNewLabelRequest}
          />
        ))}
        {/* Drop-Animations-Placeholder: 30-px-Zeile als nächster Slot.
            Bei collapsed-State NICHT rendern — sonst läge der Placeholder
            im max-height:0-Container → Bounding-Rect wäre falsch. Ohne
            Placeholder schlägt das Handler-Lookup fehl → Outcome fade-out
            am Cursor-Punkt. */}
        {!isCollapsed && (
          <div
            className="card-drop-placeholder card-drop-placeholder--compact"
            data-placeholder-category={category}
            style={{ height: 30 }}
          />
        )}
      </div>
    </div>
  );
}

// --- CompactView: Hauptkomponente ---

export default function CompactView({
  externalDrop,
  deck,
  isOwnProfile,
  onCategoryChange,
  canEditQuantity = false,
  onQuantityDelta,
  sortBy = 'name',
  searchTerm = '',
  highlightedCmc = null,
  strategy,
  matchingCardIds = null,
  isDraggingCategory = false,
  onRegisterDndHandlers,
  showExcluded = true,
  onRemoveCardsByCategory,
  onShowDetails,
  onSwitchPrint,
  stickyTop = 0,
  highlightedNewCardId,
  deckLabels,
  getCardLabels,
  onAssignLabel,
  onRemoveLabel,
  onNewLabelRequest,
  onDropError,
  onCategoryRenamed,
  onCardMoved,
  onCatalogCardDrop,
}) {
  const { authToken } = useAuth();
  const [previewCard, setPreviewCard] = useState(null);
  const [mousePos, setMousePos] = useState(null);

  // Hook für Deck-Karten-Aktionen (setAsCommander) — wie in Stacks/Rows/Table.
  const { setAsCommander } = useDeckCardActions({
    deck,
    canEdit: isOwnProfile,
    onError: onDropError,
  });

  // --- Hooks: view state, transparency, drag handlers ---

  const { groups, categories, moveCategory, removeCategory, addCategory, renameCategory, excludedCategoryNames } = useDeckViewState({
    deck, sortBy, strategy,
    showExcluded, isOwnProfile, authToken,
  });

  const shouldBeTransparent = useShouldBeTransparent({
    highlightedCmc, searchTerm, matchingCardIds,
  });

  const isFilterActive = !!(searchTerm?.trim() || highlightedCmc !== null || matchingCardIds !== null);

  const {
    dropTarget,
    handleSetAsCommander,
    applyManualOrder,
    handleRemoveCategory,
    pendingDeleteCategory,
    confirmDeleteCategory,
    cancelDeleteCategory,
  } = useDeckDragHandlers({
    deck, isOwnProfile, onCategoryChange, categories,
    moveCategory, removeCategory, addCategory, sortBy,
    setAsCommander,
    onRemoveCardsByCategory, onRegisterDndHandlers,
    cardWidth: null,
    onDragStartExtra: () => { setPreviewCard(null); setMousePos(null); },
    strategy, excludedCategoryNames, onDropError, onCardMoved, onCatalogCardDrop,
  });

  // Frisch gedroppte Karten landen am Ende der Ziel-Kategorie — analog zu den
  // anderen Views. Filter-Modus dimmt nicht-passende Karten nicht aus, sondern
  // entfernt sie aus der Ansicht (gleiche Konvention wie Stacks/Rows/Table).
  const groupsFiltered = useMemo(() => {
    return groups.map((g) => {
      const ordered = applyManualOrder(g.name, g.cards);
      const cards = isFilterActive ? ordered.filter((card) => !shouldBeTransparent(card)) : ordered;
      return { ...g, cards };
    });
  }, [groups, applyManualOrder, isFilterActive, shouldBeTransparent]);

  // --- Hover preview ---

  const handlePreviewStart = useCallback((card, pos) => {
    setPreviewCard(card);
    setMousePos(pos);
  }, []);

  const handlePreviewEnd = useCallback(() => {
    setPreviewCard(null);
    setMousePos(null);
  }, []);

  // --- Render ---

  if (!deck) {
    return <div className="compact-view-empty">Kein Deck geladen</div>;
  }

  const categoryIds = groupsFiltered.map((g) => `cat-${g.name}`);

  return (<>
    <div className="compact-view">
      <SortableContext items={categoryIds} strategy={verticalListSortingStrategy}>
        <div className="compact-view__columns">
          {groupsFiltered.map((group) => {
            const cards = group.cards;
            const externesZiel = externalDrop && group.id != null && !group.isCommander;

            return (
              <CompactCategory
                key={group.name}
                category={group.name}
                categoryId={group.id}
                excluded={!!(group.excluded || group.type === 'excluded')}
                cards={cards}
                isOwnProfile={isOwnProfile}
                canEditQuantity={canEditQuantity}
                onQuantityDelta={onQuantityDelta}
                onSetAsCommander={handleSetAsCommander}
                onShowDetails={onShowDetails}
                onSwitchPrint={onSwitchPrint}
                onPreviewStart={handlePreviewStart}
                onPreviewEnd={handlePreviewEnd}
                activePreviewCardId={previewCard?.id ?? null}
                isDraggingCategory={isDraggingCategory}
                isDropTarget={dropTarget === group.name || (externesZiel && externalDrop.target === group.name)}
                externalDragging={Boolean(externesZiel && externalDrop.active)}
                externalDropProps={externesZiel ? externalDrop.propsFor(group.name, group.id) : undefined}
                isDroppable={strategy.editable || !!group.excluded || group.type === 'excluded'}
                highlightedNewCardId={highlightedNewCardId}
                deckLabels={deckLabels}
                getCardLabels={getCardLabels}
                onAssignLabel={onAssignLabel}
                onRemoveLabel={onRemoveLabel}
                onNewLabelRequest={onNewLabelRequest}
                canRemove={strategy.editable}
                handleRemoveCategory={handleRemoveCategory}
                stickyTop={stickyTop}
                strategy={strategy}
                renameCategory={renameCategory}
                onCategoryRenamed={onCategoryRenamed}
              />
            );
          })}
        </div>
      </SortableContext>

      <CardPreview card={previewCard} position={mousePos} />
    </div>
    <ConfirmDialog
      open={!!pendingDeleteCategory}
      onOpenChange={(next) => { if (!next) cancelDeleteCategory(); }}
      onConfirm={confirmDeleteCategory}
      title="Kategorie löschen"
      description="Wenn du diese Kategorie löschst, werden alle enthaltenen Karten aus dem Deck entfernt."
      confirmLabel="Löschen"
      tone="danger"
    />
  </>);
}
